/**
 * Atlas Tea Co. — Newsletter AJAX Handler
 * assets/atlas-newsletter.js
 *
 * Intercepts Atlas newsletter forms, submits via fetch,
 * and handles success / duplicate / error states inline
 * without a page reload or URL param.
 *
 * Depends on markup using these data attributes:
 *   [data-atlas-newsletter]        — outer wrapper
 *   [data-atlas-newsletter-body]   — visible form elements (faded out on success)
 *   [data-atlas-newsletter-input]  — the email input
 *   [data-atlas-newsletter-submit] — the submit button
 *   [data-atlas-newsletter-success]— hidden success message
 *   [data-atlas-newsletter-error]  — hidden error message
 *
 * The <form> itself is generated by Shopify's {% form 'customer' %} tag
 * and sits inside the [data-atlas-newsletter] wrapper.
 */

(function () {
  'use strict';

  /* Prevent double-init if multiple sections load this file */
  if (window.__atlasNewsletterInit) return;
  window.__atlasNewsletterInit = true;

  var SEL = {
    wrapper: '[data-atlas-newsletter]',
    body:    '[data-atlas-newsletter-body]',
    input:   '[data-atlas-newsletter-input]',
    submit:  '[data-atlas-newsletter-submit]',
    success: '[data-atlas-newsletter-success]',
    error:   '[data-atlas-newsletter-error]'
  };

  /* ── Initialise every Atlas newsletter wrapper on the page ── */
  function init() {
    var wrappers = document.querySelectorAll(SEL.wrapper);
    wrappers.forEach(function (wrapper) {
      var form = wrapper.querySelector('form');
      if (!form || form.dataset.atlasNewsBound) return;
      form.dataset.atlasNewsBound = '1';
      form.addEventListener('submit', function (e) {
        handleSubmit(e, wrapper);
      });
    });
  }

  /* ── Submit handler ── */
  function handleSubmit(e, wrapper) {
    e.preventDefault();

    var form     = e.currentTarget;
    var input    = wrapper.querySelector(SEL.input);
    var button   = wrapper.querySelector(SEL.submit);
    var bodyEl   = wrapper.querySelector(SEL.body);
    var successEl = wrapper.querySelector(SEL.success);
    var errorEl  = wrapper.querySelector(SEL.error);

    /* Reset previous error */
    if (errorEl) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    }

    /* Client-side validation */
    if (!input || !input.value.trim() || !input.validity.valid) {
      input.focus();
      return;
    }

    /* Loading state */
    var originalLabel = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.innerHTML = '<span aria-hidden="true">&hellip;</span>';

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { 'Accept': 'text/html' }
    })
      .then(function (response) {
        return response.text().then(function (html) {
          return { url: response.url, html: html };
        });
      })
      .then(function (result) {
        /*
         * Shopify success: the redirect lands on a URL containing
         * "customer_posted=true". On duplicate email the page re-renders
         * with form error markup instead.
         */
        var isSuccess = (
          result.url.indexOf('customer_posted=true') !== -1 ||
          result.html.indexOf('customer_posted=true') !== -1
        );

        if (isSuccess) {
          fadeToSuccess(bodyEl, successEl);
          return;
        }

        /*
         * Not a success redirect — most likely the email already exists.
         * Show a friendly "already subscribed" message rather than an error.
         */
        var isDuplicate = (
          result.html.indexOf('form--error') !== -1 ||
          result.html.indexOf('errors') !== -1
        );

        if (isDuplicate) {
          fadeToSuccess(bodyEl, successEl, true);
        } else {
          showError(errorEl, button, originalLabel,
            'Something went wrong — please try again.');
        }
      })
      .catch(function () {
        showError(errorEl, button, originalLabel,
          'Unable to connect. Please check your connection and try again.');
      });
  }

  /* ── Fade form body out, reveal success message ── */
  function fadeToSuccess(bodyEl, successEl, isDuplicate) {
    if (!bodyEl) return;

    bodyEl.style.transition = 'opacity 0.35s ease';
    bodyEl.style.opacity = '0';

    setTimeout(function () {
      bodyEl.style.display = 'none';

      if (successEl) {
        /*
         * If this is a duplicate submission, swap to the
         * "already on the list" copy stored in data-duplicate-message,
         * falling back to the default success text.
         */
        if (isDuplicate && successEl.dataset.duplicateMessage) {
          successEl.textContent = successEl.dataset.duplicateMessage;
        }

        successEl.style.display = 'block';
        /* Force reflow so the opacity transition fires */
        void successEl.offsetHeight;
        successEl.style.opacity = '1';
      }
    }, 350);
  }

  /* ── Show an inline error below the form ── */
  function showError(errorEl, button, originalLabel, message) {
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = originalLabel;
    }
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /*
   * Re-init when Shopify's Theme Editor adds/moves sections.
   * This event fires in the Customiser only.
   */
  document.addEventListener('shopify:section:load', init);
})();